import { chefKeys } from '@/lib/api/query-keys'
import type { QueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AuthApi } from '@/lib/api/auth'
import { foodApi } from '@/lib/api/food'
import { orderApi } from '@/lib/api/order'
import { shippingApi } from '@/lib/api/shipping'
import { reportApi } from '@/lib/api/report'
import { commentApi } from '@/lib/api/comments'

export function profileQueryOptions() {
  return queryOptions({ queryKey: chefKeys.profile, queryFn: async ({ signal }) => {
    try { return (await AuthApi.profile(signal)).data } catch (error) {
      if (isAxiosError(error) && [401, 403].includes(error.response?.status ?? 0)) return null
      throw error
    }
  }, retry: false })
}
export function useProfileQuery() {
  return useQuery(profileQueryOptions())
}
export function useAverageScoreQuery(enabled: boolean) {
  return useQuery({ queryKey: chefKeys.score, queryFn: async ({ signal }) => (await AuthApi.avgScore(signal)).data, enabled })
}
export function useFoodsQuery() {
  return useQuery({ queryKey: chefKeys.foods, queryFn: async ({ signal }) => (await foodApi.list(signal)).data })
}
export function useEstimateQuery() {
  return useQuery({ queryKey: chefKeys.estimate, queryFn: async ({ signal }) => (await foodApi.estimate(signal)).data })
}
export function useOrdersQuery() {
  return useQuery({ queryKey: chefKeys.orders, queryFn: async ({ signal }) => {
    const { data } = await orderApi.activeOrders(signal)
    return { enterpriseList: data.enterpriseList ?? [], foodList: data.foodList ?? [] }
  } })
}
export function useShippingQuery(from: number, to: number) {
  return useQuery({ queryKey: chefKeys.shipping(from, to), queryFn: async ({ signal }) => {
    const { data } = await shippingApi.list(from, to, signal)
    return Array.isArray(data) ? data : []
  } })
}
export function reportQueryOptions<T>(source: keyof typeof reportApi, from: number, to: number, enabled = true) {
  return queryOptions<T>({ queryKey: chefKeys.report(source, from, to), queryFn: async ({ signal }) => (await reportApi[source]<T>(from, to, signal)).data, enabled })
}
export function useReportQuery<T>(source: keyof typeof reportApi, from: number, to: number, enabled = true) {
  return useQuery(reportQueryOptions<T>(source, from, to, enabled))
}
export function commentsQueryOptions(filter: string) {
  return infiniteQueryOptions({
    queryKey: [...chefKeys.comments, filter], initialPageParam: 1,
    queryFn: async ({ pageParam, signal }) => (await commentApi.list(filter, pageParam, 30, signal)).data ?? [],
    getNextPageParam: (last, _pages, page) => last.length === 30 ? page + 1 : undefined,
  })
}
export function useCommentsQuery(filter: string) {
  return useInfiniteQuery(commentsQueryOptions(filter))
}
export function updateFoodPriceOptions(client: QueryClient) {
  return { mutationFn: ({ foodId, price }: { foodId: string; price: number }) => foodApi.updatePrice(foodId, price),
    onSuccess: () => { void client.invalidateQueries({ queryKey: chefKeys.foods }) },
  }
}
export function replyCommentOptions(client: QueryClient) {
  return { mutationFn: ({ orderId, reply }: { orderId: string; reply: string }) => commentApi.reply(orderId, reply),
    onSuccess: () => { void client.invalidateQueries({ queryKey: chefKeys.comments }) },
  }
}
export function useUpdateFoodPrice() {
  return useMutation(updateFoodPriceOptions(useQueryClient()))
}
export function useReplyComment() {
  return useMutation(replyCommentOptions(useQueryClient()))
}
